import { useCallback } from 'react';
import { MessageDisplay } from '../types/chat-hook-types';
import { logInfo, logError, LogCategory, startMeasure, endMeasure } from '@/utils/logger';
import { MessageCrudService } from '../services/messages/message-crud.service';
import { transformCopilotMessagesToDisplay } from '../utils/messageTransformers';

/**
 * Hook for loading messages from the database
 * Now optimized to load recent messages first for faster initial display
 */
export function useMessageLoading(
  setMessages: (messages: MessageDisplay[] | ((prev: MessageDisplay[]) => MessageDisplay[])) => void,
  setIsLoading: (isLoading: boolean) => void,
  setError: (error: string | null) => void
) {
  /**
   * Load recent messages for a thread (optimized for initial chat display)
   * Loads most recent 20 messages first, then user can scroll up for more
   */
  const loadMessages = useCallback(async (threadId: string, limit: number = 20) => {
    try {
      setIsLoading(true);
      const startTime = startMeasure('loadRecentMessages');
      
      logInfo('Loading recent messages for thread', {
        category: LogCategory.DATABASE,
        data: { threadId, limit }
      });

      // Use optimized recent messages fetching for faster initial load
      const copilotMessages = await MessageCrudService.fetchRecentMessages(threadId, limit);
      
      // Transform CopilotMessage[] to MessageDisplay[] format
      // This extracts assistant data from metadata and places it at the top level
      const displayMessages = transformCopilotMessagesToDisplay(copilotMessages);
      
      logInfo('Recent messages loaded successfully', {
        category: LogCategory.DATABASE,
        data: { 
          threadId, 
          messageCount: displayMessages.length,
          messagesWithAssistants: displayMessages.filter(m => m.assistant).length,
          isOptimizedLoad: true
        }
      });

      setMessages(displayMessages);
      endMeasure(startTime, 'Recent messages loading complete', LogCategory.DATABASE);
    } catch (error: any) {
      logError('Failed to load recent messages', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId }
      });
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [setMessages, setIsLoading, setError]);

  /**
   * Load all messages for a thread (legacy method for backward compatibility)
   * @deprecated Use loadMessages with default limit for better performance
   */
  const loadAllMessages = useCallback(async (threadId: string) => {
    try {
      setIsLoading(true);
      const startTime = startMeasure('loadAllMessages');
      
      logInfo('Loading all messages for thread (legacy)', {
        category: LogCategory.DATABASE,
        data: { threadId }
      });

      // Use the deprecated fetchMessages method for full conversation load
      const copilotMessages = await MessageCrudService.fetchMessages(threadId);
      const displayMessages = transformCopilotMessagesToDisplay(copilotMessages);
      
      logInfo('All messages loaded successfully', {
        category: LogCategory.DATABASE,
        data: { 
          threadId, 
          messageCount: displayMessages.length,
          messagesWithAssistants: displayMessages.filter(m => m.assistant).length,
          isLegacyLoad: true
        }
      });

      setMessages(displayMessages);
      endMeasure(startTime, 'All messages loading complete', LogCategory.DATABASE);
    } catch (error: any) {
      logError('Failed to load all messages', {
        category: LogCategory.DATABASE,
        data: { error: error.message, threadId }
      });
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [setMessages, setIsLoading, setError]);

  return { 
    loadMessages, // Now loads recent messages by default (optimized)
    loadAllMessages // Legacy method for full conversation load
  };
}
